import { Search, Wrench } from 'lucide-react';
import { useState } from 'react';
import { useFieldArray, useWatch, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Shared/Button/Button';
import { Input } from '../../Shared/Input/Input';
import { Switch } from '../../Shared/Switch/Switch';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/Domain/components/Shared/Form/Form';
import { RemoteMcpForm } from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpActionForm.hook';

type Props = {
  form: UseFormReturn<RemoteMcpForm>;
  isHidden: boolean;
  name: 'allowed_tools';
};

const SEARCH_THRESHOLD = 6;

export const RemoteMcpActionTools = ({ form, isHidden, name }: Props) => {
  const { t } = useTranslation('remote_mcp');
  const { fields } = useFieldArray({ control: form.control, name });
  const watched = useWatch({ control: form.control, name }) ?? [];
  const [query, setQuery] = useState('');

  if (isHidden) return null;

  const total = fields.length;
  const activeCount = watched.filter((tool) => tool?.is_active).length;

  const setActive = (idx: number, value: boolean) =>
    form.setValue(`${name}.${idx}.is_active`, value, { shouldDirty: true });
  const setAll = (value: boolean) =>
    form.setValue(
      name,
      watched.map((tool) => ({ ...tool, is_active: value })),
      { shouldDirty: true },
    );

  const rows = fields
    .map((field, idx) => ({
      id: field.id,
      idx,
      toolName: watched[idx]?.name ?? '',
      active: !!watched[idx]?.is_active,
    }))
    .filter((row) => row.toolName.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <section className="flex flex-col gap-3" data-testid="remote-mcp-tools">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <h3 className="text-sm font-medium text-foreground">{t('tools.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {total === 0
              ? t('tools.none_yet')
              : t('tools.enabled_count', { active: activeCount, total })}
          </p>
        </div>
        {total > 0 && (
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => setAll(true)}>
              {t('tools.enable_all')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setAll(false)}>
              {t('tools.disable_all')}
            </Button>
          </div>
        )}
      </div>

      {total === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-8 text-center">
          <span className="flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Wrench className="size-4" />
          </span>
          <p className="text-sm text-muted-foreground">{t('tools.empty')}</p>
        </div>
      ) : (
        <>
          {total > SEARCH_THRESHOLD && (
            <Input
              startIcon={<Search className="size-4 text-foreground-subtle" />}
              placeholder={t('tools.search_placeholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              data-testid="remote-mcp-tools-search"
            />
          )}
          <div className="max-h-80 overflow-y-auto rounded-xl border border-border">
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                {t('tools.no_matches')}
              </p>
            ) : (
              rows.map(({ id, idx, toolName, active }) => (
                <div
                  key={id}
                  className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 last:border-b-0"
                >
                  <span className="truncate font-mono text-sm text-foreground">{toolName}</span>
                  <Switch
                    size="sm"
                    checked={active}
                    onCheckedChange={(value) => setActive(idx, value)}
                  />
                </div>
              ))
            )}
          </div>
        </>
      )}
      <FormField
        name="default_tool_is_active"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-muted/40 p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-sm">{t('form.default_tool_is_active_label')}</FormLabel>
                <FormDescription className="text-xs">
                  {t('form.default_tool_is_active_description')}
                </FormDescription>
              </div>
              <FormControl className="self-center">
                <Switch
                  data-testid="remote-mcp-default-tool-active"
                  size="sm"
                  checked={field.value}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </FormControl>
            </div>
          </FormItem>
        )}
      />
    </section>
  );
};
